import { useCallback, useContext } from "react";

import { RouteBuilders, FormType } from "@simplyk/common";

import { FrontendFormContext } from "../contexts/FrontendFormContext";
import { useLocaleContext } from "../contexts/LocaleContext";

import { useUrlBuilder } from "./useUrlBuilder";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { DonationFormPaymentInput } from "@/common/types/donationForm";
import { TicketingPaymentInput } from "@/common/types/ticketing";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { isTicketingData } from "@/components/PaymentProcessor/helper";

interface GetRegistrationRedirectUrlParams {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  commandId?: string | null;
}

export const useRegistrationRedirect = () => {
  const { isEmbedV2, embedId } = useContext(FrontendFormContext);
  const { isoLocale } = useLocaleContext();
  const { formObject: ticketing, isCampaignRegistration } = useTicketingFormV2ContextOrDefaults();
  const buildUrl = useUrlBuilder();

  // Returns true if the user is paying a registration on a P2P ticketing form
  const shouldRedirectToPeerToPeer = useCallback(
    (
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput,
      formType: FormType
    ): paymentInput is TicketingPaymentInput => isTicketingData(paymentInput, formType) && isCampaignRegistration,
    [isCampaignRegistration]
  );

  // Returns the URL to which the user will be redirected after paying a P2P registration
  const getRegistrationRedirectUrl = useCallback(
    ({ firstName, lastName, email, commandId }: GetRegistrationRedirectUrlParams): string | null => {
      if (!ticketing.donationForm?.campaignId) {
        return null;
      }

      const params = {
        openFundraiser: "true",
        firstName: firstName || "",
        lastName: lastName || "",
        email: email || "",
        commandId: commandId || "",
        paid: "true",
        ...(isEmbedV2 && embedId && { embedId }),
      };

      return buildUrl({
        route: RouteBuilders.buildPeerToPeerV2CampaignLink({
          type: "general",
          isoLocale,
          path: ticketing.donationForm.campaignId,
          isEmbedV2,
        }),
        searchParams: params,
        includeRouterSearchParams: false,
        asExternalLink: true,
      });
    },
    [ticketing, isoLocale, buildUrl, isEmbedV2, embedId]
  );

  return {
    shouldRedirectToPeerToPeer,
    getRegistrationRedirectUrl,
  };
};
